<template>
  <BaseHeaderCommon v-bind="attrs" />
  <DevOnly>
    <ClientOnly>
      <IsolationRoot />
      <template #fallback>
        <Skeleton height="557px" />
      </template>
    </ClientOnly>
  </DevOnly>
  <Teleport to="#teleports">
    <BaseFooterCommon v-if="isSSR()" />
    <template v-else-if="!isNavSSR">
      <BaseFooterCommon v-if="isFooterVisible" />
    </template>
    <LazyBaseFooterCommon v-else />
  </Teleport>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { defineAsyncComponent, ref, computed, watch } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import Skeleton from '@nuxt-common/atomic-ui/app/components/skeleton/Skeleton';
import { useProviders } from '~/composables/useProviders';
import { GET_ATTRS } from '#shared/types/getters';
import { isSSR } from '#shared/utils/isSSR';
import isCitiRedirect from '~/utils/citiRedirect';
import onUserInit from '~/events/onUserInit';
import BaseFooterCommon from '~/components/BaseFooter.common';

const ROOT_MARGIN_SSR = '50px';
const ROOT_MARGIN_CSR = '200px';

useProviders();

const navHeader = useNavHeader();
const { isNavSSR } = useEnvProps();
const attrs = computed(() => navHeader[GET_ATTRS]);
const isFooterVisible = ref(false);
const isCreditCardRedirection = ref(false);

const LazyBaseFooterCommon = defineAsyncComponent({
  loader: () => Promise.resolve(BaseFooterCommon),
  hydrate: (hydrate) => {
    if (isCreditCardRedirection.value || isFooterVisible.value) {
      hydrate();
      return;
    }

    const cleanups = [];
    const doHydrate = () => {
      hydrate();
      cleanups.forEach((cleanup) => cleanup());
    };

    const stopWatchRedirect = watch(isCreditCardRedirection, (active) => {
      if (active) {
        doHydrate();
      }
    });
    cleanups.push(stopWatchRedirect);

    const stopWatchVisibility = watch(isFooterVisible, (visible) => {
      if (visible) {
        doHydrate();
      }
    });
    cleanups.push(stopWatchVisibility);
  },
});

if (!isSSR()) {
  // credit card flow: redirecting user always on homepage after signin
  if (isNavSSR) {
    onUserInit(() => {
      isCreditCardRedirection.value = isCitiRedirect();
    });
  }

  const teleportsEl = document.querySelector('#teleports');

  if (!teleportsEl) {
    isFooterVisible.value = true;
  } else {
    const { stop } = useIntersectionObserver(
      teleportsEl,
      ([entry]) => {
        if (entry?.isIntersecting) {
          isFooterVisible.value = true;
          stop();
        }
      },
      {
        rootMargin: isNavSSR ? ROOT_MARGIN_SSR : ROOT_MARGIN_CSR,
      },
    );
  }
}
</script>
